import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "@/lib/api";

const AuthContext = createContext(null);

// SECURITY: this context never touches localStorage/sessionStorage. The JWT is
// stored exclusively in an HttpOnly, Secure, SameSite=Strict cookie set by the
// backend. The browser sends it automatically on same-site requests; JS code
// (and therefore any XSS payload) cannot read or persist it.
//
// Session state is derived from `/api/auth/me`, which reads the cookie and
// returns the authenticated user plus an `impersonating` flag.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data.user);
      setImpersonating(!!data.impersonating);
    } catch {
      setUser(null);
      setImpersonating(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // The backend sets the HttpOnly cookie on successful verification; the
  // response body intentionally does NOT include the token.
  const login = async (email, code) => {
    const { data } = await API.post("/auth/verify-code", { email, code });
    setUser(data.user);
    setImpersonating(false);
    return data.user;
  };

  const sendCode = async (email) => {
    const { data } = await API.post("/auth/send-code", { email });
    return data;
  };

  const logout = async () => {
    try { await API.post("/auth/logout"); } catch {
      // Even if the request fails, drop local state so the UI shows logged out.
    }
    setUser(null);
    setImpersonating(false);
  };

  // Impersonation is fully managed server-side: the new JWT (issued by the
  // backend) embeds the original admin's id as a claim, so exit-impersonation
  // can restore the admin session without any client-side bookkeeping.
  const impersonate = async (userId) => {
    const { data } = await API.post(`/admin/impersonate/${userId}`);
    setUser(data.user);
    setImpersonating(true);
    return data.user;
  };

  const exitImpersonation = async () => {
    try {
      const { data } = await API.post("/admin/impersonate/exit");
      setUser(data.user);
      setImpersonating(false);
    } catch {
      // If the backend can't restore the admin session, fall back to logout.
      setUser(null);
      setImpersonating(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, sendCode, logout, checkAuth,
               impersonate, exitImpersonation, impersonating }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
